import React from 'react';
import { Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2, ShieldCheck } from 'lucide-react';
import AdminAccessPanel from '../components/AdminAccessPanel';
import { useAuth } from '../context/AuthContext';

/**
 * Superadmin-only page for managing who holds admin access.
 *
 * Gated twice over: this component refuses to render for anyone but the superadmin, and every
 * action the panel performs is re-authorised server-side in the `manage-admin-access` edge
 * function. Reaching this URL directly therefore grants nothing — the redirect below is a
 * courtesy, not the control.
 *
 * Mirrors `CampaignPage`'s gate exactly, including waiting for the admin lookup to resolve, so a
 * superadmin never sees a flash of "unauthorised" while their session is still restoring.
 */
const AdminAccessPage: React.FC = () => {
  const { isSuperadmin, adminCheckLoading, adminStatusResolved, user, loading: authLoading } = useAuth();

  const awaitingAdminDecision = authLoading || (Boolean(user?.email) && !adminStatusResolved);

  if (awaitingAdminDecision || adminCheckLoading) {
    return (
      <div className="min-h-[calc(100vh-4rem)] bg-gradient-to-br from-light-gray to-sky-blue/20 flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl p-8 text-center border border-white/50 max-w-md w-full"
        >
          <Loader2 className="w-12 h-12 text-electric-blue mx-auto mb-4 animate-spin" />
          <p className="text-slate-gray">Checking access…</p>
        </motion.div>
      </div>
    );
  }

  if (!authLoading && adminStatusResolved && !isSuperadmin) {
    return (
      <Navigate
        to="/unauthorized?reason=admin-access"
        replace
        state={{
          message:
            'Managing admin access is limited to the superadmin. Contact support if you believe you should have access.',
        }}
      />
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-gradient-to-br from-light-gray to-sky-blue/20">
      <div className="w-full max-w-[min(100%,96rem)] mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col min-h-[calc(100vh-5rem)]">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-2xl border border-electric-blue/20 bg-white shadow-sm flex flex-col flex-1 min-h-0 p-6 sm:p-8"
        >
          <header className="mb-6 pb-4 border-b border-charcoal/10 shrink-0">
            <h1 className="text-2xl sm:text-3xl font-bold text-charcoal flex items-center gap-3">
              <ShieldCheck className="w-8 h-8 text-electric-blue shrink-0" aria-hidden />
              Admin access
            </h1>
            <p className="mt-2 text-sm text-slate-gray leading-relaxed max-w-3xl">
              Invite new admins by email and remove them when they no longer need access.
            </p>
          </header>

          <AdminAccessPanel currentUserEmail={user?.email ?? ''} />
        </motion.div>
      </div>
    </div>
  );
};

export default AdminAccessPage;
